import React, { useEffect, useState } from "react";
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
} from "@fluentui/react";
import useAxios from "axios-hooks";
import { axiosInstance } from "../../../../api/config";
import env from "../../../../env";
import { STATUS, STATUS_LABELS, describePeriod, extractErrorMessage } from "./streakUtils";

const { REACT_APP_API_BASE_URI } = env;

const StreakPeriodDialog = ({ memberId, history, period, onDismiss, onChanged }) => {
    const [error, setError] = useState(null);

    const [{ loading: saving }, execute] = useAxios(
        { baseURL: REACT_APP_API_BASE_URI },
        { axios: axiosInstance, manual: true }
    );

    useEffect(() => {
        setError(null);
    }, [period]);

    if (!period) {
        return null;
    }

    const cadence = history.streakCadence;
    const isMissed = period.status === STATUS.MISSED;

    const patchPeriod = async () => {
        setError(null);
        try {
            await execute({
                url: `2020-05-05/internal/streak/${memberId}/patches`,
                method: "POST",
                data: { date: period.startDate },
            });
            onChanged(`Streak ${cadence} patched.`);
        } catch (err) {
            setError(extractErrorMessage(err, "Unable to patch this streak period."));
        }
    };

    return (
        <Dialog
            hidden={false}
            onDismiss={saving ? undefined : onDismiss}
            minWidth={460}
            dialogContentProps={{
                type: DialogType.normal,
                title: isMissed ? `Patch streak ${cadence}` : describePeriod(period, cadence),
                subText: isMissed ? describePeriod(period, cadence) : undefined,
            }}
            modalProps={{ isBlocking: saving }}
        >
            <Stack tokens={{ childrenGap: 12 }}>
                <Text>
                    <strong>Status:</strong> {STATUS_LABELS[period.status]}
                    {period.activityCount > 0 && ` (${period.activityCount} qualifying ${period.activityCount === 1 ? "activity" : "activities"})`}
                </Text>

                {isMissed && (
                    <>
                        <Text>
                            No qualifying activity was recorded for this {cadence}. Patching inserts a manual streak
                            patch so this {cadence} counts toward the member's streak. Patches are permanent and cannot be
                            removed from the admin.
                        </Text>
                        {period.streakIfPatched != null && (
                            <Text>
                                <strong>Current streak:</strong> {history.currentStreak} →{" "}
                                <strong>{period.streakIfPatched}</strong> after patch
                            </Text>
                        )}
                    </>
                )}

                {period.patches.length > 0 && (
                    <Stack tokens={{ childrenGap: 8 }}>
                        <Text><strong>Patches</strong></Text>
                        {period.patches.map((patch) => (
                            <Text key={patch.id}>
                                Added by {patch.patchedBy || "automatic streak repair"}
                            </Text>
                        ))}
                    </Stack>
                )}

                {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            </Stack>

            <DialogFooter>
                {saving && <Spinner size={SpinnerSize.small} styles={{ root: { display: "inline-block", marginRight: 8 } }} />}
                {isMissed && <PrimaryButton text={`Patch streak ${cadence}`} onClick={patchPeriod} disabled={saving} />}
                <DefaultButton text="Close" onClick={onDismiss} disabled={saving} />
            </DialogFooter>
        </Dialog>
    );
};

export default StreakPeriodDialog;
